export const INACTIVE_PLAN_MESSAGE =
  "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information";

/**
 * CLE-2675 — the payer answered, but every benefit it disclosed is out-of-network. The
 * normalized values are zeros of ignorance, not a real $0 plan, so the form is not
 * auto-filled and the user is pointed at the payer (additionalInfo usually carries the
 * payer's own phone number).
 */
export const buildOutOfNetworkOnlyBenefitsMessage = (
  additionalInfo?: string | null
) =>
  `The payer returned only out-of-network benefit information for this plan, so the benefit fields were not filled in and an estimate should not be created from this response. Please contact the payer to verify the patient's in-network benefits, then enter them manually.${
    additionalInfo ? ` Payer info: ${additionalInfo}` : ""
  }`;

/**
 * The message a STORED eligibility response warrants, or null when it warrants none.
 *
 * This is the single source of truth for the four effects (primary/secondary ×
 * fresh-response/reopened-order) that re-derive the eligibility message from
 * `benefitDetails`. Before it existed, each effect had its own copy of the rules and
 * none of them knew about `hasOnlyOutOfNetworkBenefits` — so the effect watching
 * `benefitDetails` fired right after EligibilityButton stored the response and wiped
 * the guard message the button had just set (CLE-2675).
 */
export const deriveStoredEligibilityMessage = (
  response: any
): string | null => {
  if (!response) {
    return null;
  }
  if (response.planStatus === "Inactive") {
    return INACTIVE_PLAN_MESSAGE;
  }
  if (response.hasOnlyOutOfNetworkBenefits) {
    return buildOutOfNetworkOnlyBenefitsMessage(response.additionalInfo);
  }
  const allFieldsAreNull =
    response.familyCalendarYearDeductible === null &&
    response.individualCalendarYearDeductible === null &&
    response.remainingDeductible === null &&
    response.coinsurancePercent === null;
  if (response.isEligibilityEmpty || allFieldsAreNull) {
    return response.additionalInfo || null;
  }
  return null;
};

/**
 * CLE-2683 — the account has declared itself out of network for this payer/plan. `benefitsRepointed`
 * distinguishes the two very different situations that produces: either the payer sent
 * out-of-network figures and these numbers are them, or it sent none and these are still the
 * in-network numbers on a plan we are out of network for.
 */
export const buildNetworkStatusMessage = (
  override?: { status: string; benefitsRepointed: boolean } | null
): string | null => {
  if (!override || override.status !== "OutOfNetwork") return null;
  return override.benefitsRepointed
    ? "This facility is out of network for this plan. The benefits below are the payer's out-of-network figures."
    : "This facility is out of network for this plan, but the payer only reported in-network benefits. The figures below are those in-network numbers — confirm the patient's out-of-network cost with the payer before quoting.";
};
